import { CheckOutlined, CopyOutlined } from '@ant-design/icons'
import { Button } from 'antd'
import { useEffect, useRef, useState } from 'react'

import { CopyPayload, copyToClipboard } from './assistantCopy'
import { KW } from './ui'

/** How long "Copied" stays next to the button. */
const COPIED_NOTICE_MS = 2000

interface Props {
  /** Built when the button is pressed: the answer as it is on screen at that moment. */
  getPayload: () => CopyPayload
}

/** Copies an answer (see assistantCopy.ts) and says briefly that it did. */
const CopyAnswerButton = ({ getPayload }: Props) => {
  const [outcome, setOutcome] = useState<'copied' | 'failed' | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(timerRef.current), [])

  const onCopy = async () => {
    const copied = await copyToClipboard(getPayload())
    setOutcome(copied ? 'copied' : 'failed')
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setOutcome(null), COPIED_NOTICE_MS)
  }

  return (
    <>
      <Button
        type="text"
        size="small"
        title="Copy"
        aria-label="Copy answer"
        icon={outcome === 'copied' ? <CheckOutlined /> : <CopyOutlined />}
        onClick={onCopy}
      />
      {/* Always present, so that a screen reader announces the text when it appears. Shown
        last in the row (order-last), after the rating buttons: the text comes and goes, and
        must not move a button the user is about to click. */}
      <span
        role="status"
        className="order-last text-xs"
        style={{ color: KW.muted }}
      >
        {outcome === 'copied'
          ? 'Copied'
          : outcome === 'failed'
          ? 'Could not copy'
          : ''}
      </span>
    </>
  )
}

export default CopyAnswerButton
